import {
    Building2,
    CalendarDays,
    Clock3,
    ClipboardList,
    Dumbbell,
    FileText,
    LayoutDashboard,
    MailPlus,
    Tag,
    Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface NavItem {
    to: string;
    /** i18n key for the sidebar label and the header title. */
    labelKey: string;
    /** i18n key for the header subtitle. */
    subtitleKey: string;
    icon: LucideIcon;
    /**
     * Extra terms (both locales) matched by the sidebar search, so typing
     * "gaji" or "payroll" both find the Contract menu.
     */
    keywords: string[];
}

export interface NavGroup {
    id: string;
    /** i18n key for the group heading in the sidebar. */
    labelKey: string;
    items: NavItem[];
}

/* -------------------------------------------------------------------------- */
/* Items                                                                      */
/* -------------------------------------------------------------------------- */

const DASHBOARD: NavItem = {
    to: "/dashboard",
    labelKey: "nav.dashboard.label",
    subtitleKey: "nav.dashboard.subtitle",
    icon: LayoutDashboard,
    keywords: [
        "dashboard",
        "stats",
        "statistik",
        "chart",
        "grafik",
        "performance",
        "performa",
    ],
};

/** Kalender mingguan — "kapan" sesi terjadi. */
const PLAN: NavItem = {
    to: "/plan",
    labelKey: "nav.plan.label",
    subtitleKey: "nav.plan.subtitle",
    icon: CalendarDays,
    keywords: ["session", "sesi", "schedule", "jadwal", "calendar", "kalender", "week", "minggu"],
};

/** Tabel penugasan — "apa saja" sesinya + aksi cepat. */
const ASSIGNMENTS: NavItem = {
    to: "/assignments",
    labelKey: "nav.assignments.label",
    subtitleKey: "nav.assignments.subtitle",
    icon: ClipboardList,
    keywords: ["assignment", "penugasan", "task", "tugas", "list", "daftar", "table", "tabel"],
};

const AFFILIATIONS: NavItem = {
    to: "/affiliations",
    labelKey: "nav.affiliations.label",
    subtitleKey: "nav.affiliations.subtitle",
    icon: Building2,
    keywords: ["affiliation", "afiliasi", "outlet", "merchant", "club", "klub"],
};

const SPORTS: NavItem = {
    to: "/sports",
    labelKey: "nav.sports.label",
    subtitleKey: "nav.sports.subtitle",
    icon: Dumbbell,
    keywords: ["sport", "olahraga", "badminton", "futsal", "tennis", "category"],
};

const AVAILABILITY: NavItem = {
    to: "/availability",
    labelKey: "nav.availability.label",
    subtitleKey: "nav.availability.subtitle",
    icon: Clock3,
    keywords: ["time", "waktu", "weekly", "mingguan", "hours", "jam", "available", "tersedia"],
};

const CONTRACT: NavItem = {
    to: "/contract",
    labelKey: "nav.contract.label",
    subtitleKey: "nav.contract.subtitle",
    icon: FileText,
    keywords: ["payroll", "gaji", "salary", "agreement", "kontrak", "payment"],
};

const PRICING: NavItem = {
    to: "/pricing",
    labelKey: "nav.pricing.label",
    subtitleKey: "nav.pricing.subtitle",
    icon: Tag,
    keywords: ["pricing", "price", "harga", "tarif", "service", "layanan", "rate"],
};

const PAYROLL: NavItem = {
    to: "/payroll",
    labelKey: "nav.payroll.label",
    subtitleKey: "nav.payroll.subtitle",
    icon: Wallet,
    keywords: ["payroll", "gaji", "salary", "estimasi", "income", "penghasilan"],
};

/**
 * Undangan klub — kebalikan dari menu Afiliasi: merchant/klub yang mengajukan,
 * COACH yang memutuskan. Khusus permintaan ber-jalur `coach`.
 */
const INVITATIONS: NavItem = {
    to: "/invitations",
    labelKey: "nav.invitations.label",
    subtitleKey: "nav.invitations.subtitle",
    icon: MailPlus,
    keywords: [
        "invitation",
        "undangan",
        "request",
        "permintaan",
        "join",
        "gabung",
        "affiliation",
        "afiliasi",
    ],
};

/**
 * Menu dikelompokkan per section supaya jelas bedanya:
 *  - Overview  : ringkasan performa
 *  - Schedule  : jadwal (kalender mingguan) vs penugasan (tabel)
 *  - Profile   : kemampuan coach — olahraga & ketersediaan
 *  - Merchant  : hubungan dengan merchant — afiliasi + semua soal uang
 *                (kontrak, harga layanan, payroll)
 */
export const NAV_GROUPS: NavGroup[] = [
    {
        id: "overview",
        labelKey: "nav.group.overview",
        items: [DASHBOARD],
    },
    {
        id: "schedule",
        labelKey: "nav.group.schedule",
        items: [PLAN, ASSIGNMENTS, AVAILABILITY],
    },
    {
        id: "profile",
        labelKey: "nav.group.profile",
        items: [SPORTS],
    },
    {
        id: "merchant",
        labelKey: "nav.group.merchant",
        items: [AFFILIATIONS, INVITATIONS, CONTRACT, PRICING, PAYROLL],
    },
];

/** Flat list — dipakai header & pencarian global. */
export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

export function findNavItem(pathname: string): NavItem | undefined {
    return NAV_ITEMS.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
}
